import { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { installerApi } from '@/lib/api';
import { toast } from 'sonner';
import { Plus, Search, Edit, Trash2, Phone, Mail } from 'lucide-react';

export default function Installers() {
  const [installers, setInstallers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingInstaller, setEditingInstaller] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
  });

  const fetchData = async () => {
    try {
      const res = await installerApi.getAll();
      setInstallers(res.data);
    } catch (error) {
      console.error('Error fetching installers:', error);
      toast.error('Failed to load installers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // Data fetch runs once on mount
  }, []);

  const openModal = (installer = null) => {
    if (installer) {
      setEditingInstaller(installer);
      setFormData({
        name: installer.name,
        phone: installer.phone || '',
        email: installer.email || '',
      });
    } else {
      setEditingInstaller(null);
      setFormData({
        name: '',
        phone: '',
        email: '',
      });
    }
    setIsModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingInstaller) {
        await installerApi.update(editingInstaller.id, formData);
        toast.success('Installer updated successfully');
      } else {
        await installerApi.create(formData);
        toast.success('Installer added successfully');
      }
      setIsModalOpen(false);
      fetchData();
    } catch (error) {
      console.error('Error saving installer:', error);
      toast.error('Failed to save installer');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this installer?')) return;
    try {
      await installerApi.delete(id);
      toast.success('Installer deleted successfully');
      fetchData();
    } catch (error) {
      console.error('Error deleting installer:', error);
      toast.error('Failed to delete installer');
    }
  };

  const filteredInstallers = installers.filter(installer =>
    installer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (installer.email && installer.email.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-muted-foreground">Loading installers...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="installers-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold font-heading">Installers</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage your installation team
          </p>
        </div>
        <Button className="rounded-sm" onClick={() => openModal()} data-testid="btn-add-installer">
          <Plus className="w-4 h-4 mr-2" />
          Add Installer
        </Button>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Search installers..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10 rounded-sm"
          data-testid="installer-search"
        />
      </div>

      {/* Installers Table */}
      <Card className="border border-border shadow-none rounded-sm">
        <CardContent className="p-0">
          {filteredInstallers.length === 0 ? (
            <div className="py-12 text-center">
              <p className="text-muted-foreground">No installers found</p>
              <Button variant="link" onClick={() => openModal()} className="mt-2">
                Add your first installer
              </Button>
            </div>
          ) : (
            <Table data-testid="installers-table">
              <TableHeader>
                <TableRow className="bg-muted/50 hover:bg-muted/50">
                  <TableHead className="font-medium">Name</TableHead>
                  <TableHead className="font-medium">Phone</TableHead>
                  <TableHead className="font-medium">Email</TableHead>
                  <TableHead className="w-[100px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredInstallers.map((installer) => (
                  <TableRow key={installer.id} data-testid={`installer-row-${installer.id}`}>
                    <TableCell className="font-medium">{installer.name}</TableCell>
                    <TableCell>
                      {installer.phone && (
                        <div className="flex items-center gap-1.5 text-muted-foreground">
                          <Phone className="w-3.5 h-3.5" />
                          <span className="font-mono-data text-sm">{installer.phone}</span>
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      {installer.email && (
                        <div className="flex items-center gap-1.5 text-muted-foreground">
                          <Mail className="w-3.5 h-3.5" />
                          <span className="text-sm">{installer.email}</span>
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => openModal(installer)}
                          data-testid={`btn-edit-installer-${installer.id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive"
                          onClick={() => handleDelete(installer.id)}
                          data-testid={`btn-delete-installer-${installer.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-md" data-testid="installer-modal">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editingInstaller ? 'Edit Installer' : 'Add New Installer'}
            </DialogTitle>
            <DialogDescription>
              {editingInstaller ? 'Update installer contact details.' : 'Add a new installer to your team.'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="rounded-sm mt-1.5"
                required
                data-testid="input-installer-name"
              />
            </div>
            <div>
              <Label htmlFor="phone">Phone</Label>
              <Input
                id="phone"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="rounded-sm mt-1.5 font-mono-data"
                placeholder="04XX XXX XXX"
                data-testid="input-installer-phone"
              />
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="rounded-sm mt-1.5"
                data-testid="input-installer-email"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)} className="rounded-sm">
                Cancel
              </Button>
              <Button type="submit" className="rounded-sm" data-testid="btn-save-installer">
                {editingInstaller ? 'Update Installer' : 'Add Installer'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
